import type { DefinitionsMetadataApi } from '../api/DefinitionsMetadataApi'
import type { CustomMetric, CustomMetricDraft } from '../models/metric.types'
import { chipifyFormula, extractMetrics, tokenize, validate } from '../service/formula'

export type CustomMetricSaveMode = 'create' | 'update'
export type CustomMetricValidationError = 'name' | 'description' | 'formula' | 'unknown-metric'

export interface SaveCustomMetricDependencies {
  api: Pick<DefinitionsMetadataApi, 'createCustomMetric' | 'updateCustomMetric'>
  refreshAttributes: () => Promise<boolean>
  invalidateCatalog: (metric: CustomMetric, previousField?: string) => void
}

export type SaveCustomMetricResult =
  | { ok: true; metric: CustomMetric; refreshFailed: boolean }
  | { ok: false; error: CustomMetricValidationError }

export class SaveCustomMetricFlow {
  constructor(private readonly dependencies: SaveCustomMetricDependencies) {}

  async save(
    draft: CustomMetricDraft,
    mode: CustomMetricSaveMode,
    workspaceId: string | number,
    allowedMetrics: readonly string[],
    original?: CustomMetric,
  ): Promise<SaveCustomMetricResult> {
    const name = draft.metric_name.trim()
    if (name.length < 3) return { ok: false, error: 'name' }
    if (!draft.description.trim()) return { ok: false, error: 'description' }
    if (!draft.formula.trim()) return { ok: false, error: 'formula' }
    const requiredMetrics = extractMetrics(draft.formula, allowedMetrics)
    const identifiers = draft.formula.match(/[A-Za-z_$][A-Za-z0-9_.$-]*/gu) ?? []
    if (identifiers.some((identifier) => !requiredMetrics.includes(identifier))) {
      return { ok: false, error: 'unknown-metric' }
    }
    const chipFormula = chipifyFormula(draft.formula, allowedMetrics)
    const tokens = tokenize(chipFormula)
    if (
      tokens.map((token) => token.value).join('') !== chipFormula.replace(/\s+/gu, '') ||
      validate(tokens)
    ) {
      return { ok: false, error: 'formula' }
    }

    const metric: CustomMetric = {
      ...draft,
      metric_name: mode === 'update' && original ? original.metric_name : name,
      ...(mode === 'update' ? { new_metric_name: name } : {}),
      formula: draft.formula.trim(),
      required_metrics: requiredMetrics,
      description: draft.description.trim(),
      workspace_id: Number(workspaceId),
      ...(original?.id === undefined ? {} : { id: original.id }),
    }
    if (mode === 'update') await this.dependencies.api.updateCustomMetric(metric)
    else await this.dependencies.api.createCustomMetric(metric)
    const refreshFailed = !(await this.dependencies.refreshAttributes())
    const saved = { ...metric, metric_name: name }
    this.dependencies.invalidateCatalog(saved, original?.metric_name)
    return { ok: true, metric: saved, refreshFailed }
  }
}
